import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, LifeBuoy } from "lucide-react";
import { z } from "zod";

const TOPIC_LABELS = {
  general: "General question about the platform",
  account: "Account, login, or profile",
  photo_requirement: "Mandatory 2-photo requirement question",
  trust_safety: "Trust & safety report (fraud, ban evasion, harassment)",
  ban_appeal: "Ban Appeal",
  press: "Press or partnerships",
} as const;
type TopicKey = keyof typeof TOPIC_LABELS;

export const Route = createFileRoute("/contact")({
  validateSearch: z.object({
    topic: z
      .enum(["general", "account", "photo_requirement", "trust_safety", "ban_appeal", "press"])
      .optional(),
  }),
  head: () => ({
    meta: [
      { title: "Contact & Support — Want To Buy" },
      { name: "description", content: "Get help with the Want To Buy connection platform — account issues, the mandatory photo requirement, and trust & safety reports. Payment or transaction disputes must be handled through your payment provider." },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { topic: topicParam } = Route.useSearch();
  const initial: TopicKey = topicParam ?? "general";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<string>(TOPIC_LABELS[initial]);
  const [orderId, setOrderId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.includes("@") || message.trim().length < 10) {
      toast.error("Please fill in your name, a valid email, and a message of at least 10 characters.");
      return;
    }
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      setName(""); setEmail(""); setMessage(""); setTopic("General question about the platform"); setOrderId("");
      toast.success("Support request submitted — we'll reply within 1 business day.");
    }, 600);
  }

  return (
    <DocPage
      eyebrow="Support"
      title="Contact & Support"
      intro="Questions about how the connection platform works, the mandatory photo requirement, your account, or a trust & safety concern? Send us a message."
    >
      <section className="panel-note !mt-0">
        <p className="!mt-0">
          <strong>Payment or transaction disputes.</strong> Want To Buy does not mediate disputes between users. If a transaction goes wrong, please raise it directly with your payment provider — for PayPal Goods &amp; Services, use the PayPal Resolution Centre. See the <a href="/payment-safety">Payment Safety page</a> for guidance and how to use your Want To Buy message thread as evidence.
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <Card icon={<MessageSquare className="h-4 w-4" />} title="How the platform works" body="Buy orders, offers, the mandatory 2-photo scam filter." />
        <Card icon={<LifeBuoy className="h-4 w-4" />} title="Trust & safety" body="Report fraud, ban evasion, harassment, or counterfeits." />
        <Card icon={<Mail className="h-4 w-4" />} title="Account" body="Login issues, profile changes, verification." />
      </section>

      <form onSubmit={submit} className="panel space-y-4 p-5">
        <h2 className="!mt-0">Send a support request</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Your name"><input value={name} onChange={(e) => setName(e.target.value)} className="input-base" /></Field>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-base" /></Field>
        </div>
        <Field label="Topic">
          <select value={topic} onChange={(e) => setTopic(e.target.value)} className="input-base">
            {Object.values(TOPIC_LABELS).map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </Field>
        <Field label="Order ID (if applicable)">
          <input value={orderId} onChange={(e) => setOrderId(e.target.value)} className="input-base" placeholder="e.g. Charizard PSA 9 · #8801" />
        </Field>
        <Field label="Message">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="input-base !h-auto min-h-[140px] py-2" placeholder="Tell us what's going on — include order IDs or usernames if relevant." />
        </Field>
        <div className="flex justify-end">
          <button disabled={busy} className="btn-primary px-4 py-2 text-sm disabled:opacity-60">{busy ? "Sending…" : "Send request"}</button>
        </div>
      </form>

      <style>{`.input-base { width:100%; height:2.5rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.75rem; font-size: 0.875rem; color: var(--color-foreground); }
      .input-base:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in oklab, var(--color-primary) 30%, transparent); }`}</style>
    </DocPage>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Card({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-md border border-border bg-panel/60 p-4">
      <div className="flex items-center gap-2 text-primary">{icon}<span className="text-sm font-semibold text-foreground">{title}</span></div>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}
